import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TermsDialog from "./TermsDialog";
import { TERMS_CONTENT } from "./termsContent";

const FREE_EMAIL_PROVIDERS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "icloud.com",
  "mail.com",
  "protonmail.com",
  "zoho.com",
  "yandex.com",
  "gmx.com",
  "live.com",
  "msn.com",
];

const signUpSchema = z.object({
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
  email: z
    .string()
    .email("Please enter a valid email address")
    .refine((email) => {
      const domain = email.split("@")[1]?.toLowerCase();
      return !FREE_EMAIL_PROVIDERS.includes(domain);
    }, "Please use your business email address"),
  itRole: z.string().min(2, "IT role must be at least 2 characters"),
  colleagueEmails: z.string().optional(),
  acceptedTerms: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions",
  }),
});

type SignUpFormData = z.infer<typeof signUpSchema>;

async function generateHash(content: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

export default function SignUpForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      fullName: "",
      companyName: "",
      email: "",
      itRole: "",
      colleagueEmails: "",
      acceptedTerms: false,
    },
  });

  const acceptedTerms = watch("acceptedTerms");

  const onSubmit = async (data: SignUpFormData) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const termsHash = await generateHash(TERMS_CONTENT);

      const response = await fetch("/api/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName: data.fullName,
          companyName: data.companyName,
          email: data.email,
          itRole: data.itRole,
          colleagueEmails: data.colleagueEmails || "",
          termsVersion: "2.1",
          termsHash,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to submit application");
      }

      setSubmitStatus({
        type: "success",
        message:
          "Application submitted successfully! We'll contact you if selected for this phase of research.",
      });
      reset();
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message:
          error instanceof Error ? error.message : "Failed to submit application",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTermsAccept = () => {
    setValue("acceptedTerms", true);
    setTermsDialogOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
      e.preventDefault();
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown} className="space-y-5">
        <div>
          <label className="block text-white text-sm font-medium mb-2">
            Full Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            {...register("fullName")}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0193F8] focus:border-transparent transition-all"
            placeholder="John Smith"
          />
          {errors.fullName && (
            <p className="text-red-400 text-sm mt-1">{errors.fullName.message}</p>
          )}
        </div>

        <div>
          <label className="block text-white text-sm font-medium mb-2">
            Company Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            {...register("companyName")}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0193F8] focus:border-transparent transition-all"
            placeholder="Acme Corporation"
          />
          {errors.companyName && (
            <p className="text-red-400 text-sm mt-1">{errors.companyName.message}</p>
          )}
        </div>

        <div>
          <label className="block text-white text-sm font-medium mb-2">
            Business Email <span className="text-red-400">*</span>
          </label>
          <input
            type="email"
            {...register("email")}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0193F8] focus:border-transparent transition-all"
            placeholder="you@company.com"
          />
          {errors.email && (
            <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
          )}
          <p className="text-gray-500 text-xs mt-1">
            Free email providers (Gmail, Yahoo, etc.) are not accepted
          </p>
        </div>

        <div>
          <label className="block text-white text-sm font-medium mb-2">
            IT Role <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            {...register("itRole")}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0193F8] focus:border-transparent transition-all"
            placeholder="IT Director, System Administrator, etc."
          />
          {errors.itRole && (
            <p className="text-red-400 text-sm mt-1">{errors.itRole.message}</p>
          )}
        </div>

        <div>
          <label className="block text-white text-sm font-medium mb-2">
            Colleague Emails <span className="text-gray-500">(optional)</span>
          </label>
          <input
            type="text"
            {...register("colleagueEmails")}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0193F8] focus:border-transparent transition-all"
            placeholder="colleague1@company.com, colleague2@company.com"
          />
          <p className="text-gray-500 text-xs mt-1">
            Invite colleagues to join the research program (comma-separated)
          </p>
        </div>

        <div className="flex items-start gap-3 pt-2">
          <input
            type="checkbox"
            id="terms"
            checked={acceptedTerms}
            readOnly
            onClick={() => {
              if (!acceptedTerms) {
                setTermsDialogOpen(true);
              } else {
                setValue("acceptedTerms", false);
              }
            }}
            className="mt-1 w-4 h-4 rounded border-white/20 bg-white/10 text-[#0193F8] focus:ring-[#0193F8] focus:ring-offset-0 cursor-pointer"
          />
          <label className="text-gray-300 text-sm">
            I have read and agree to the{" "}
            <button
              type="button"
              onClick={() => setTermsDialogOpen(true)}
              className="text-[#0193F8] hover:text-[#0173C8] underline transition-colors"
            >
              Terms & Conditions and Confidentiality Agreement
            </button>
            <span className="text-red-400"> *</span>
          </label>
        </div>
        {errors.acceptedTerms && (
          <p className="text-red-400 text-sm">{errors.acceptedTerms.message}</p>
        )}

        {submitStatus && (
          <div
            className={`p-4 rounded-lg ${
              submitStatus.type === "success"
                ? "bg-green-500/20 border border-green-500/30 text-green-300"
                : "bg-red-500/20 border border-red-500/30 text-red-300"
            }`}
          >
            {submitStatus.message}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3 px-4 bg-[#0193F8] hover:bg-[#0173C8] disabled:bg-[#0193F8]/50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Submitting...
            </>
          ) : (
            "Submit Application"
          )}
        </button>
      </form>

      <TermsDialog
        open={termsDialogOpen}
        onClose={() => setTermsDialogOpen(false)}
        onAccept={handleTermsAccept}
      />
    </>
  );
}
